import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * RC10m · Medical Card Expiry acknowledgment modal.
 *
 * Two-step: full verbatim RC10m Rule-2 wording + required
 * acknowledgment checkbox. The Save button is disabled until the
 * checkbox is ticked. On confirm, the caller re-fires the assignment
 * request with `medical_expiry_acknowledged: true`.
 *
 * Props:
 *   driverName        — display name for the driver
 *   roleLabel         — "primary driver" | "co-driver" | "fill-in"
 *   medicalExpiration — ISO date string (YYYY-MM-DD) from server
 *   tourName          — Tour display name (not sensitive)
 *   busy              — pending state (disable Save during inflight)
 *   onCancel          — () => void
 *   onConfirm         — () => Promise<void>
 */
function fmtMMDDYYYY(iso) {
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}

export default function MedicalExpiryAcknowledgmentModal({
  driverName,
  roleLabel,
  medicalExpiration,
  tourName,
  busy,
  onCancel,
  onConfirm,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const inflight = useRef(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const expMMDDYYYY = fmtMMDDYYYY(medicalExpiration);

  const handleSave = async () => {
    if (inflight.current || !acknowledged || busy) return;
    inflight.current = true;
    try {
      await onConfirm();
    } finally {
      inflight.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-carbon/70 flex items-center justify-center px-4"
      data-testid="rc10m-ack-modal"
      onClick={() => { if (!busy) onCancel(); }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-md w-full max-w-md border border-amber-hold shadow-xl"
      >
        <div className="bg-amber-hold text-carbon px-4 py-3 flex items-center justify-between">
          <div className="text-[14px] font-semibold" data-testid="rc10m-ack-title">
            Medical Card Renewal Required
          </div>
          <button
            type="button"
            onClick={() => { if (!busy) onCancel(); }}
            data-testid="rc10m-ack-close"
            className="text-carbon hover:text-black"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[13px] text-carbon leading-relaxed">
          <div data-testid="rc10m-ack-wording">
            This driver's Medical Card expires on{" "}
            <span className="font-mono tabular font-semibold" data-testid="rc10m-ack-date">
              {expMMDDYYYY}
            </span>{" "}during this Tour. The assignment is allowed for planning, but the renewed
            expiration date must be recorded before the driver operates after that date.
          </div>
          <div className="text-[12px] text-steel border-t border-coach-silver pt-2">
            <div>Driver: <span className="text-carbon font-medium">{driverName || "—"}</span></div>
            <div>Role: <span className="text-carbon">{roleLabel || "—"}</span></div>
            {tourName && (
              <div>Tour: <span className="text-carbon">{tourName}</span></div>
            )}
          </div>
          <label className="flex items-start gap-2 text-[12.5px] text-carbon border-t border-coach-silver pt-3">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={busy}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="rc10m-ack-checkbox"
              className="mt-0.5"
            />
            <span>
              I acknowledge the renewed expiration must be recorded before the driver operates
              after that date.
            </span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-coach-silver bg-road-white rounded-b-md">
          <button
            type="button"
            onClick={() => { if (!busy) onCancel(); }}
            disabled={busy}
            data-testid="rc10m-ack-cancel"
            className="px-3 py-1.5 text-[12.5px] border border-coach-silver rounded-sm text-steel hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !acknowledged}
            data-testid="rc10m-ack-save"
            className="px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save Assignment"}
          </button>
        </div>
      </div>
    </div>
  );
}
